import { create } from 'zustand';
import {
  InventoryOverviewSessionRow,
  fetchInventoryOverview,
} from '../services/inventory/inventoryService';

/** Feed page size — matches the server's default `limit`. */
export const INVENTORY_OVERVIEW_FEED_PAGE_SIZE = 30;

/**
 * All-restaurants inventory overview store.
 *
 * Deliberately separate from `appInventoryStore`, which is strictly single-restaurant
 * (`currentRestaurantId`, `is*LoadedFor` guards): writing multi-restaurant rows into it
 * would corrupt those guards for the normal inventory page. Same precedent as
 * `appWasteNeedsStore` for the all-restaurants dashboard card.
 *
 * The feed is fetched UNFILTERED — the page's restaurant filter narrows the loaded rows
 * client-side, so switching it never refetches (same convention as the dashboard).
 */
interface InventoryOverviewState {
  /** Most recent session per (restaurant, group). */
  latest: InventoryOverviewSessionRow[];
  /** Every session newest-first, keyset-paginated. */
  feed: InventoryOverviewSessionRow[];
  feedHasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  loadedOnce: boolean;
  error: string | null;
  /** Fires the `latest` table and the first `feed` page in parallel. */
  load: () => Promise<void>;
  /** Appends the next feed page, keyed off the last loaded row's `completedAt`. */
  loadMoreFeed: () => Promise<void>;
  reset: () => void;
}

const initialState = {
  latest: [] as InventoryOverviewSessionRow[],
  feed: [] as InventoryOverviewSessionRow[],
  feedHasMore: false,
  isLoading: false,
  isLoadingMore: false,
  loadedOnce: false,
  error: null as string | null,
};

export const useInventoryOverviewStore = create<InventoryOverviewState>((set, get) => ({
  ...initialState,

  load: async () => {
    if (get().isLoading) return;
    set({ isLoading: true, error: null });
    try {
      const [latest, feed] = await Promise.all([
        fetchInventoryOverview({ mode: 'latest' }),
        fetchInventoryOverview({ mode: 'feed', limit: INVENTORY_OVERVIEW_FEED_PAGE_SIZE }),
      ]);
      set({
        latest: latest.rows,
        feed: feed.rows,
        feedHasMore: feed.hasMore,
        isLoading: false,
        loadedOnce: true,
      });
    } catch (err) {
      console.error('[ERROR] loading inventory overview', err);
      set({ isLoading: false, loadedOnce: true, error: 'Could not load inventory overview' });
    }
  },

  loadMoreFeed: async () => {
    const { feed, feedHasMore, isLoadingMore } = get();
    if (!feedHasMore || isLoadingMore || feed.length === 0) return;
    const beforeDate = feed[feed.length - 1].completedAt.toISOString();
    set({ isLoadingMore: true });
    try {
      const page = await fetchInventoryOverview({
        mode: 'feed',
        limit: INVENTORY_OVERVIEW_FEED_PAGE_SIZE,
        beforeDate,
      });
      set({
        feed: [...get().feed, ...page.rows],
        feedHasMore: page.hasMore,
        isLoadingMore: false,
      });
    } catch (err) {
      console.error('[ERROR] loading more inventory overview sessions', err);
      set({ isLoadingMore: false });
    }
  },

  reset: () => set({ ...initialState }),
}));
